<template>
  <div id="app" class="min-h-screen bg-dark-900 text-gray-100">
    <!-- Header -->
    <header class="sticky top-0 z-50 bg-dark-900/80 backdrop-blur-lg border-b border-dark-700">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between h-16">
          <!-- Logo and Navigation -->
          <div class="flex items-center space-x-8">
            <RouterLink to="/" class="flex items-center space-x-2">
              <div class="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg"></div>
              <span class="text-xl font-bold">ADIC Explorer</span>
            </RouterLink>
            
            <nav class="hidden md:flex items-center space-x-1">
              <!-- Primary Navigation -->
              <RouterLink
                v-for="item in primaryNavItems"
                :key="item.path"
                :to="item.path"
                class="px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-dark-800/50 transition-all"
                active-class="text-white bg-primary-500/20"
              >
                {{ item.name }}
              </RouterLink>

              <!-- Foundation Layer Dropdown -->
              <div class="relative group">
                <button class="px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-dark-800/50 transition-all flex items-center space-x-1">
                  <span>Foundation</span>
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div class="absolute left-0 mt-2 w-48 bg-dark-900 border border-dark-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div class="py-2">
                    <RouterLink
                      v-for="item in foundationNavItems"
                      :key="item.path"
                      :to="item.path"
                      class="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-dark-800/50 transition-colors"
                      active-class="text-primary-400 bg-dark-800/30"
                    >
                      <div class="flex items-center space-x-2">
                        <span>{{ item.icon }}</span>
                        <span>{{ item.name }}</span>
                      </div>
                    </RouterLink>
                  </div>
                </div>
              </div>

              <!-- Secondary Navigation -->
              <RouterLink
                v-for="item in secondaryNavItems"
                :key="item.path"
                :to="item.path"
                class="px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-dark-800/50 transition-all"
                active-class="text-white bg-primary-500/20"
              >
                {{ item.name }}
              </RouterLink>
            </nav>
          </div>

          <!-- Actions -->
          <div class="flex items-center space-x-4">
            <button
              @click="toggleSearch"
              class="p-2 rounded-lg hover:bg-dark-800 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            
            <button
              v-if="!isConnected"
              @click="connectWallet"
              class="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              Connect Wallet
            </button>
            
            <div v-else class="flex items-center space-x-2">
              <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span class="text-sm text-gray-400">{{ shortAddress }}</span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Search Modal -->
    <Transition name="modal">
      <div
        v-if="searchOpen"
        class="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
        @click="closeSearch"
      >
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

        <div
          class="relative w-full max-w-2xl bg-dark-900 rounded-xl border border-dark-700 shadow-2xl"
          @click.stop
        >
          <!-- Search Input -->
          <div class="p-4 border-b border-dark-700">
            <div class="relative">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                placeholder="Search by address, transaction hash, or message ID..."
                class="w-full pl-10 pr-4 py-3 bg-dark-800 text-white rounded-lg border border-dark-600 focus:border-primary-500 focus:outline-none"
                @keyup.enter="performSearch"
                @keyup.esc="closeSearch"
              />
              <button
                v-if="searchQuery"
                @click="searchQuery = ''"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Search Tips -->
            <div class="mt-3 flex flex-wrap gap-2 text-xs text-gray-400">
              <span>Press</span>
              <kbd class="px-2 py-0.5 bg-dark-800 border border-dark-600 rounded">Enter</kbd>
              <span>to search or</span>
              <kbd class="px-2 py-0.5 bg-dark-800 border border-dark-600 rounded">Esc</kbd>
              <span>to close</span>
            </div>
          </div>

          <!-- Search Results -->
          <div class="max-h-96 overflow-y-auto">
            <!-- Loading State -->
            <div v-if="isSearching" class="p-8 text-center">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-2"></div>
              <p class="text-gray-400">Searching...</p>
            </div>

            <!-- No Query -->
            <div v-else-if="!searchQuery" class="p-8">
              <div class="text-center mb-6">
                <svg class="w-12 h-12 mx-auto mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p class="text-gray-400 mb-2">Search ADIC Explorer</p>
                <p class="text-sm text-gray-500">Find addresses, transactions, or messages</p>
              </div>

              <div class="space-y-2">
                <div class="text-xs font-semibold text-gray-500 uppercase mb-2">What you can search for:</div>
                <div class="space-y-2">
                  <div class="flex items-center space-x-2 p-2 bg-dark-800/50 rounded">
                    <span class="text-primary-400">💼</span>
                    <span class="text-sm text-gray-300">Wallet Addresses</span>
                  </div>
                  <div class="flex items-center space-x-2 p-2 bg-dark-800/50 rounded">
                    <span class="text-green-400">💱</span>
                    <span class="text-sm text-gray-300">Transaction Hashes</span>
                  </div>
                  <div class="flex items-center space-x-2 p-2 bg-dark-800/50 rounded">
                    <span class="text-blue-400">📨</span>
                    <span class="text-sm text-gray-300">Message IDs</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Search Results -->
            <div v-else-if="searchResults.length > 0" class="divide-y divide-dark-800">
              <RouterLink
                v-for="result in searchResults"
                :key="result.id"
                :to="result.path"
                class="block p-4 hover:bg-dark-800 transition-colors"
                @click="closeSearch"
              >
                <div class="flex items-center space-x-3">
                  <div class="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg"
                       :class="result.type === 'address' ? 'bg-primary-500/20' :
                               result.type === 'transaction' ? 'bg-green-500/20' : 'bg-blue-500/20'">
                    <span class="text-xl">{{ result.icon }}</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center space-x-2 mb-1">
                      <span class="px-2 py-0.5 text-xs font-medium rounded"
                            :class="result.type === 'address' ? 'bg-primary-500/20 text-primary-400' :
                                    result.type === 'transaction' ? 'bg-green-500/20 text-green-400' :
                                    'bg-blue-500/20 text-blue-400'">
                        {{ result.type }}
                      </span>
                      <span v-if="result.label" class="text-xs text-gray-500">{{ result.label }}</span>
                    </div>
                    <code class="text-sm text-gray-300 font-mono break-all">{{ result.value }}</code>
                  </div>
                  <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </RouterLink>
            </div>

            <!-- No Results -->
            <div v-else-if="searchQuery && !isSearching" class="p-8 text-center">
              <svg class="w-12 h-12 mx-auto mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p class="text-gray-400 mb-1">No results found</p>
              <p class="text-sm text-gray-500">Try searching for an address, transaction, or message ID</p>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Main Content -->
    <main class="container mx-auto px-4 py-8">
      <RouterView v-slot="{ Component }">
        <Transition name="fade" mode="out-in">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </main>

    <!-- Status Bar -->
    <footer class="fixed bottom-0 left-0 right-0 bg-dark-900/80 backdrop-blur-lg border-t border-dark-700">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between h-10 text-xs text-gray-400">
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-1">
              <div :class="connectionStatusClass" class="w-2 h-2 rounded-full"></div>
              <span>{{ connectionStatus }}</span>
            </div>
            <span>Height: {{ networkHeight }}</span>
            <span>TPS: {{ tps }}</span>
          </div>
          
          <div class="flex items-center space-x-4">
            <span>Messages: {{ totalMessages }}</span>
            <span>Finalized: {{ finalizedCount }}</span>
            <span>v{{ appVersion }}</span>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { RouterLink, RouterView, useRouter } from 'vue-router'
import { useNetworkStore } from '@/stores/network'
import { useWalletStore } from '@/stores/wallet'
import { useLiveUpdateManager } from '@/services/LiveUpdateManager'
import { apiClient } from '@/api/client'

// Navigation items - Layered Architecture
// Layer 0: Network Foundation
const primaryNavItems = [
  { name: 'Dashboard', path: '/' },
  { name: 'Explorer', path: '/explorer' },
  { name: 'Network', path: '/network' },
]

// Layer 1: Foundation Systems (Dropdown)
const foundationNavItems = [
  { name: 'Governance', path: '/governance', icon: '🏛️' },
  { name: 'PoUW Tasks', path: '/pouw', icon: '⚙️' },
  { name: 'Security', path: '/security', icon: '🛡️' },
  { name: 'Randomness', path: '/randomness', icon: '🎲' },
]

// Layer 2: Economics & Tools
const secondaryNavItems = [
  { name: 'Economics', path: '/economics' },
  { name: 'P-adic', path: '/padic' },
  { name: 'Analytics', path: '/analytics' },
  { name: 'API', path: '/api' },
]

// Router instance
const router = useRouter()

// Store instances
const networkStore = useNetworkStore()
const walletStore = useWalletStore()

// Reactive state
const searchOpen = ref(false)
const searchQuery = ref('')
const searchResults = ref<any[]>([])
const isSearching = ref(false)
const searchInput = ref<HTMLInputElement | null>(null)
const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0'

// Computed properties
const isConnected = computed(() => walletStore.isConnected)
const shortAddress = computed(() => {
  if (!walletStore.address) return ''
  return `${walletStore.address.slice(0, 6)}...${walletStore.address.slice(-4)}`
})

const connectionStatus = computed(() => networkStore.connectionStatus)
const connectionStatusClass = computed(() => {
  switch (networkStore.connectionStatus) {
    case 'connected':
      return 'bg-green-500'
    case 'connecting':
      return 'bg-yellow-500 animate-pulse'
    case 'disconnected':
      return 'bg-red-500'
    default:
      return 'bg-gray-500'
  }
})

const networkHeight = computed(() => networkStore.networkHeight.toLocaleString())
const tps = computed(() => networkStore.tps.toFixed(2))
const totalMessages = computed(() => networkStore.totalMessages.toLocaleString())
const finalizedCount = computed(() => networkStore.finalizedCount.toLocaleString())

// Methods
const toggleSearch = async () => {
  searchOpen.value = !searchOpen.value
  if (searchOpen.value) {
    await nextTick()
    searchInput.value?.focus()
  } else {
    searchQuery.value = ''
    searchResults.value = []
  }
}

const closeSearch = () => {
  searchOpen.value = false
  searchQuery.value = ''
  searchResults.value = []
}

const connectWallet = async () => {
  await walletStore.connect()
}

const detectSearchType = (query: string): string => {
  const cleaned = query.trim().toLowerCase()

  // Message ID or Transaction hash (64 hex chars)
  if (/^[a-f0-9]{64}$/i.test(cleaned)) {
    return 'hash'
  }

  // ADIC address (starts with adic1)
  if (cleaned.startsWith('adic1')) {
    return 'address'
  }

  // Raw hex address (64 hex chars)
  if (/^[a-f0-9]{64}$/i.test(cleaned)) {
    return 'address'
  }

  return 'unknown'
}

const performSearch = async () => {
  const query = searchQuery.value.trim()
  if (!query) return

  isSearching.value = true
  searchResults.value = []

  try {
    const searchType = detectSearchType(query)

    if (searchType === 'address' || query.startsWith('adic1')) {
      // Search for address
      try {
        const response = await apiClient.get(`/wallet/balance/${query}`)
        if (response.data) {
          searchResults.value.push({
            id: `address-${query}`,
            type: 'address',
            icon: '💼',
            value: query,
            label: `Balance: ${parseFloat(response.data.balance).toFixed(2)} ADIC`,
            path: `/address/${query}`
          })
        }
      } catch (err) {
        console.log('Address not found:', query)
      }
    }

    if (searchType === 'hash') {
      // Try as transaction hash
      try {
        const txResponse = await apiClient.get('/wallet/transactions', {
          params: { limit: 10 }
        })
        const tx = txResponse.data.transactions?.find((t: any) => t.tx_hash === query)
        if (tx) {
          searchResults.value.push({
            id: `tx-${query}`,
            type: 'transaction',
            icon: '💱',
            value: query,
            label: `${parseFloat(tx.amount).toFixed(2)} ADIC`,
            path: `/transactions` // Can enhance with direct tx view later
          })
        }
      } catch (err) {
        console.log('Transaction not found:', query)
      }

      // Try as message ID
      try {
        const msgResponse = await apiClient.get(`/message/${query}`)
        if (msgResponse.data) {
          searchResults.value.push({
            id: `msg-${query}`,
            type: 'message',
            icon: '📨',
            value: query,
            label: `Depth: ${msgResponse.data.depth || 'N/A'}`,
            path: `/message/${query}`
          })
        }
      } catch (err) {
        console.log('Message not found:', query)
      }
    }

    // If no specific match, try partial address search
    if (searchResults.value.length === 0 && query.length >= 8) {
      try {
        const walletsResponse = await apiClient.get('/wallet/balances', {
          params: { limit: 5 }
        })
        const matches = walletsResponse.data.wallets?.filter((w: any) =>
          w.address.toLowerCase().includes(query.toLowerCase())
        )

        matches?.forEach((wallet: any) => {
          searchResults.value.push({
            id: `wallet-${wallet.address}`,
            type: 'address',
            icon: '💼',
            value: wallet.address,
            label: `Balance: ${parseFloat(wallet.balance).toFixed(2)} ADIC`,
            path: `/address/${wallet.address}`
          })
        })
      } catch (err) {
        console.log('Partial address search failed:', err)
      }
    }

  } catch (error) {
    console.error('Search error:', error)
  } finally {
    isSearching.value = false
  }
}

// Watch for search query changes and auto-search after debounce
let searchTimeout: any = null
watch(searchQuery, (newQuery) => {
  if (searchTimeout) clearTimeout(searchTimeout)

  if (newQuery.length >= 8) {
    searchTimeout = setTimeout(() => {
      performSearch()
    }, 500)
  } else {
    searchResults.value = []
  }
})

// Lifecycle
onMounted(async () => {
  console.log('[App.vue] Component mounted, initializing LiveUpdateManager...')

  // Initialize LiveUpdateManager first (connects WebSocket)
  const liveUpdateManager = useLiveUpdateManager()
  await liveUpdateManager.initialize()

  // Then initialize network store (which will listen to LiveUpdateManager events)
  networkStore.initialize()

  // Listen for keyboard shortcuts
  window.addEventListener('keydown', (e) => {
    // Cmd/Ctrl + K to open search
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault()
      toggleSearch()
    }
  })
})
</script>

<style>
/* Global styles */
@import './style.css';

/* Transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from > div:last-child,
.modal-leave-to > div:last-child {
  transform: scale(0.95) translateY(-20px);
}

.modal-enter-active > div:last-child,
.modal-leave-active > div:last-child {
  transition: all 0.2s ease;
}
</style>